import type { DirectoryBrowseIndex, DirectoryBrowseItem } from '@tabel/shared/personnel';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ChevronDown, Filter, PanelRightOpen, Search, X } from 'lucide-react';
import { type KeyboardEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Field, inputCls } from '../../components/ui';
import { dateTime } from '../../lib/personnel-api';
import {
  type AttendanceQuery,
  attendancePeriod,
  attendanceQuery,
  selectedPassSites,
} from './attendance-filters';
import { NoResults, StateBadge } from './parts';

const LETTERS = [...'АБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯ', 'A–Z', '#'];
const FILTER_KEYS = [
  'q',
  'letter',
  'state',
  'site',
  'brigade',
  'employment',
  'activity',
  'duplicates',
  'sameName',
  'period',
  'passFrom',
  'passTo',
  'passSites',
];
const QUICK_KEYS = new Set(['employment', 'period', 'passFrom', 'passTo', 'passSites']);
const collator = new Intl.Collator('ru', { sensitivity: 'base', numeric: true });
const fold = (s: string) => s.normalize('NFKC').toLocaleLowerCase('ru').replace(/ё/g, 'е');
const words = (s: string) =>
  fold(s)
    .match(/[\p{L}\p{N}]+/gu)
    ?.join(' ') ?? '';
const initial = (p: DirectoryBrowseItem) => {
  const first = fold(p.displayName?.trim() ?? '')
    .slice(0, 1)
    .toLocaleUpperCase('ru');
  return /[А-Я]/u.test(first) ? first : /[A-Z]/u.test(first) ? 'A–Z' : '#';
};
const employmentLabels: Record<string, string> = {
  all: 'Все, включая уволенных',
  active: 'Работает',
  dismissed: 'Уволен по доступным источникам',
  status_conflict: 'Статус расходится',
  unknown: 'Занятость не указана',
};
const periodLabels: Record<string, string> = {
  all: 'Без ограничения',
  today: 'Сегодня',
  '7d': 'Последние 7 дней',
  '30d': 'Последние 30 дней',
  custom: 'Указать даты',
};
const activityLabels: Record<string, string> = {
  recent: 'Есть проходы за 30 дней',
  multisite: 'Несколько площадок за 30 дней',
  inactive: 'Нет проходов за 30 дней',
  never: 'Проходов пока нет',
};

export function PeopleDirectory({
  index,
  params,
  set,
  onOpen,
  fetching,
  attendance: requestedAttendance,
}: {
  index?: DirectoryBrowseIndex;
  params: URLSearchParams;
  set: (entries: Record<string, string | null>, replace?: boolean) => void;
  onOpen: (id: string) => void;
  fetching: boolean;
  attendance?: AttendanceQuery;
}) {
  const items = index?.items ?? [];
  const [expanded, setExpanded] = useState(false);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const list = useRef<HTMLDivElement>(null);
  const sitePicker = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (sitePicker.current && !sitePicker.current.contains(event.target as Node))
        sitePicker.current.open = false;
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const q = params.get('q') ?? '';
  const letter = params.get('letter') ?? '';
  const attendance = requestedAttendance ?? attendanceQuery(params, index?.asOfMs ?? Date.now());
  const period = attendancePeriod(params);
  const passSiteIds = useMemo(() => selectedPassSites(params), [params]);
  const attendanceActive = period !== 'all' || passSiteIds.length > 0;
  const rangeReady = Boolean(
    index &&
      !attendance.error &&
      (index.passRange?.fromMs ?? null) === (attendance.passFromMs ?? null) &&
      (index.passRange?.toMs ?? null) === (attendance.passToMs ?? null),
  );
  const filterCount = FILTER_KEYS.filter(
    (k) => k !== 'q' && k !== 'letter' && !QUICK_KEYS.has(k) && params.get(k),
  ).length;
  const hasFilters = FILTER_KEYS.some((k) => params.get(k));
  const update = (key: string, value: string | null) => set({ [key]: value, cursor: null });
  const reset = () => set(Object.fromEntries([...FILTER_KEYS, 'cursor'].map((k) => [k, null])));
  const showDuplicates = (nameKey?: string) =>
    set({
      ...Object.fromEntries(
        [...FILTER_KEYS, 'cursor'].filter((k) => !QUICK_KEYS.has(k)).map((k) => [k, null]),
      ),
      ...(nameKey ? { sameName: nameKey } : { duplicates: 'only' }),
    });
  const quickItems = useMemo(() => {
    if (!rangeReady) return [];
    return items.filter((p) => {
      const employment = params.get('employment');
      if (!employment && p.employmentStatus === 'dismissed') return false;
      if (employment && employment !== 'all' && p.employmentStatus !== employment) return false;
      if (period !== 'all' && !p.passSites.length) return false;
      if (passSiteIds.length && !p.passSites.some((s) => passSiteIds.includes(s.id))) return false;
      return true;
    });
  }, [items, params, rangeReady, passSiteIds, period]);
  const nameCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of quickItems) if (p.nameKey) counts.set(p.nameKey, (counts.get(p.nameKey) ?? 0) + 1);
    return counts;
  }, [quickItems]);
  const duplicateGroups = [...nameCounts.values()].filter((n) => n > 1).length;
  const sites = useMemo(
    () =>
      [...new Map(items.flatMap((p) => p.sites).map((s) => [s.id, s])).values()].sort((a, b) =>
        collator.compare(a.name, b.name),
      ),
    [items],
  );
  const brigades = useMemo(
    () =>
      [...new Map(items.flatMap((p) => p.brigades).map((b) => [b.id, b])).values()].sort((a, b) =>
        collator.compare(a.name, b.name),
      ),
    [items],
  );
  const beforeLetter = useMemo(() => {
    const needle = words(q);
    const raw = fold(q.trim());
    const threshold = (index?.asOfMs ?? 0) - (index?.windowMs ?? 30 * 86400_000);
    return quickItems
      .filter((p) => {
        if (
          raw &&
          !fold(p.id).includes(raw) &&
          (!needle || (!words(p.displayName ?? '').includes(needle) && !p.nameKey.includes(needle)))
        )
          return false;
        if (params.get('sameName') && p.nameKey !== params.get('sameName')) return false;
        if (params.get('state') && p.state !== params.get('state')) return false;
        if (params.get('site') && !p.sites.some((s) => s.id === params.get('site'))) return false;
        if (params.get('brigade') && !p.brigades.some((b) => b.id === params.get('brigade'))) return false;
        if (params.get('duplicates') === 'only' && (nameCounts.get(p.nameKey) ?? 0) < 2) return false;
        const recent = p.lastEventMs !== null && p.lastEventMs >= threshold;
        if (params.get('activity') === 'recent' && !recent) return false;
        if (params.get('activity') === 'multisite' && p.recentSiteCount < 2) return false;
        if (params.get('activity') === 'inactive' && recent) return false;
        if (params.get('activity') === 'never' && p.lastEventMs !== null) return false;
        return true;
      })
      .sort((a, b) => collator.compare(a.displayName ?? '', b.displayName ?? '') || a.id.localeCompare(b.id));
  }, [quickItems, q, params, nameCounts, index?.asOfMs, index?.windowMs]);
  const letterCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of beforeLetter) counts.set(initial(p), (counts.get(initial(p)) ?? 0) + 1);
    return counts;
  }, [beforeLetter]);
  const rows = useMemo(
    () => beforeLetter.filter((p) => !letter || initial(p) === letter),
    [beforeLetter, letter],
  );
  const virtual = useVirtualizer({
    count: rows.length,
    getScrollElement: () => list.current,
    getItemKey: (i) => rows[i]?.id ?? i,
    estimateSize: () => 132,
    overscan: 8,
    initialRect: { width: 900, height: 560 },
  });
  const virtualItems = virtual.getVirtualItems();
  const focusRowMounted = virtualItems.some((v) => v.index === focusIndex);
  const filterKey = FILTER_KEYS.map((k) => params.get(k) ?? '').join('\u0000');
  const lastFilterKey = useRef(filterKey);
  useEffect(() => {
    if (lastFilterKey.current === filterKey) return;
    lastFilterKey.current = filterKey;
    list.current?.scrollTo?.({ top: 0 });
    setFocusIndex(null);
  }, [filterKey]);
  useEffect(() => {
    if (focusIndex === null || !focusRowMounted) return;
    const frame = requestAnimationFrame(() => {
      list.current
        ?.querySelector<HTMLButtonElement>(`[data-person-index="${focusIndex}"] [data-open-person]`)
        ?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [focusIndex, focusRowMounted]);
  const navigateRows = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!rows.length || event.altKey || event.ctrlKey || event.metaKey) return;
    const parent = (event.target as HTMLElement).closest<HTMLElement>('[data-person-index]');
    const current = parent ? Number(parent.dataset.personIndex) : (focusIndex ?? -1);
    const delta = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0;
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? rows.length - 1
          : delta
            ? Math.min(rows.length - 1, Math.max(0, current + delta))
            : null;
    if (next === null) return;
    event.preventDefault();
    virtual.scrollToIndex(next, { align: 'auto' });
    setFocusIndex(next);
  };
  const selected = [
    ['letter', letter && `Фамилия на ${letter}`],
    [
      'state',
      params.get('state') === 'confirmed'
        ? 'Карточка подтверждена'
        : params.get('state') && 'Карточка предварительная',
    ],
    ['site', sites.find((s) => s.id === params.get('site'))?.name],
    ['brigade', brigades.find((b) => b.id === params.get('brigade'))?.name],
    ['employment', employmentLabels[params.get('employment') ?? '']],
    [
      'period',
      params.get('period') === 'custom'
        ? `Проходы: ${params.get('passFrom') || '…'} — ${params.get('passTo') || '…'}`
        : periodLabels[params.get('period') ?? ''],
    ],
    [
      'passSites',
      passSiteIds.length
        ? `Проходы: ${passSiteIds.map((id) => sites.find((s) => s.id === id)?.name ?? 'площадка недоступна').join(' · ')}`
        : '',
    ],
    ['activity', activityLabels[params.get('activity') ?? '']],
    ['duplicates', params.get('duplicates') && 'Есть совпадения ФИО'],
    [
      'sameName',
      params.get('sameName') &&
        `ФИО — ${items.find((p) => p.nameKey === params.get('sameName'))?.displayName ?? 'совпадающие карточки'}`,
    ],
  ].filter((entry): entry is [string, string] => Boolean(entry[1]));
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 [&_label]:mb-0">
        <div className="min-w-0 flex-1 basis-64">
          <Field label="Найти человека">
            <div className="relative">
              <Search
                size={17}
                aria-hidden
                className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-3"
              />
              <input
                aria-label="Найти человека"
                placeholder="Фамилия, имя или Company ID"
                className={`${inputCls} pl-10`}
                value={q}
                onChange={(e) => set({ q: e.target.value, cursor: null }, true)}
              />
            </div>
          </Field>
        </div>
        <Button
          icon={Filter}
          aria-expanded={expanded}
          aria-controls="personnel-filters"
          className={filterCount ? 'border-accent text-accent' : ''}
          onClick={() => setExpanded(!expanded)}
        >
          Фильтры{filterCount ? ` · ${filterCount}` : ''}
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 [&_label]:mb-0">
        <FilterSelect
          label="Статус сотрудника"
          value={params.get('employment')}
          onChange={(v) => update('employment', v)}
        >
          <option value="">Без уволенных</option>
          {Object.entries(employmentLabels).map(([key, name]) => (
            <option key={key} value={key}>
              {name}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect
          label="Период проходов"
          value={period}
          onChange={(v) => set({ period: v, passFrom: null, passTo: null, cursor: null })}
        >
          {Object.entries(periodLabels).map(([key, name]) => (
            <option key={key} value={key}>
              {name}
            </option>
          ))}
        </FilterSelect>
        <div className="min-w-0">
          <span className="mb-1.5 block text-[13px] font-medium text-ink">Площадки проходов</span>
          <details
            ref={sitePicker}
            className="relative"
            onKeyDown={(e) => {
              if (e.key === 'Escape' && sitePicker.current?.open) {
                sitePicker.current.open = false;
                sitePicker.current.querySelector('summary')?.focus();
                e.stopPropagation();
              }
            }}
          >
            <summary
              className={`${inputCls} flex cursor-pointer list-none items-center justify-between gap-2 [&::-webkit-details-marker]:hidden`}
              aria-label="Площадки проходов"
            >
              <span className="truncate">
                {passSiteIds.length === 1
                  ? (sites.find((s) => s.id === passSiteIds[0])?.name ?? 'Площадка недоступна')
                  : passSiteIds.length
                    ? `Выбрано площадок: ${passSiteIds.length}`
                    : 'Все площадки'}
              </span>
              <ChevronDown size={16} aria-hidden className="shrink-0 text-ink-3" />
            </summary>
            <div
              role="group"
              aria-label="Выбор площадок проходов"
              className="absolute right-0 z-20 mt-1 max-h-72 w-full min-w-0 overflow-y-auto rounded-card border border-line bg-surface p-2 shadow-lg"
            >
              <Button
                size="sm"
                variant="ghost"
                className="mb-1 w-full justify-start"
                onClick={() => update('passSites', null)}
              >
                Все площадки
              </Button>
              {sites.map((s) => (
                <label
                  key={s.id}
                  className="flex min-h-10 cursor-pointer items-center gap-2 rounded-control px-2 text-sm text-ink hover:bg-subtle"
                >
                  <input
                    type="checkbox"
                    checked={passSiteIds.includes(s.id)}
                    className="h-4 w-4 shrink-0 accent-accent"
                    onChange={(e) =>
                      update(
                        'passSites',
                        (e.target.checked ? [...passSiteIds, s.id] : passSiteIds.filter((id) => id !== s.id))
                          .sort()
                          .join(','),
                      )
                    }
                  />
                  <span className="break-words">{s.name}</span>
                </label>
              ))}
              {!sites.length && <p className="p-2 text-xs text-ink-3">Нет доступных площадок</p>}
            </div>
          </details>
        </div>
      </div>
      {params.get('period') === 'custom' && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&_label]:mb-0">
          <Field label="Проходы с">
            <input
              aria-label="Проходы с"
              type="date"
              className={inputCls}
              value={params.get('passFrom') ?? ''}
              onChange={(e) => update('passFrom', e.target.value)}
              aria-invalid={Boolean(attendance.error)}
            />
          </Field>
          <Field label="Проходы по">
            <input
              aria-label="Проходы по"
              type="date"
              className={inputCls}
              value={params.get('passTo') ?? ''}
              onChange={(e) => update('passTo', e.target.value)}
              aria-invalid={Boolean(attendance.error)}
            />
          </Field>
        </div>
      )}
      <p className="text-xs text-ink-3">
        По умолчанию — без уволенных, с проходами за последние 30 дней.{' '}
        {attendanceActive
          ? `Показаны люди с проходами в выбранных условиях. Даты и время: ${Intl.DateTimeFormat().resolvedOptions().timeZone}.`
          : 'Период и площадки отбираются по фактическим проходам.'}
      </p>
      {attendance.error && (
        <p role="alert" className="text-sm text-bad">
          {attendance.error}
        </p>
      )}
      {expanded && (
        <div
          id="personnel-filters"
          className="grid grid-cols-1 gap-3 rounded-card border border-line bg-subtle p-4 sm:grid-cols-2 xl:grid-cols-3 [&_label]:mb-0"
        >
          <FilterSelect
            label="Площадка регистрации"
            value={params.get('site')}
            onChange={(v) => update('site', v)}
          >
            <option value="">Все площадки</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect
            label="Бригада на сегодня"
            value={params.get('brigade')}
            onChange={(v) => update('brigade', v)}
          >
            <option value="">Все бригады</option>
            {brigades.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect
            label="Состояние карточки"
            value={params.get('state')}
            onChange={(v) => update('state', v)}
          >
            <option value="">Все карточки</option>
            <option value="provisional">Предварительная</option>
            <option value="confirmed">Подтверждена</option>
          </FilterSelect>
          <FilterSelect
            label="Активность по проходам"
            value={params.get('activity')}
            onChange={(v) =>
              set({
                activity: v,
                cursor: null,
                ...(v === 'never' || v === 'inactive' ? { period: 'all', passFrom: null, passTo: null } : {}),
              })
            }
          >
            <option value="">Любая активность</option>
            {Object.entries(activityLabels).map(([key, name]) => (
              <option key={key} value={key}>
                {name}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect
            label="Совпадения ФИО"
            value={params.get('duplicates')}
            onChange={(v) => update('duplicates', v)}
          >
            <option value="">Все имена</option>
            <option value="only">Есть другие карточки с тем же ФИО</option>
          </FilterSelect>
          <p className="text-xs text-ink-3 sm:col-span-2 xl:col-span-3">
            Фильтр «Площадка регистрации» означает наличие записи в источнике. Быстрый фильтр «Площадки
            проходов» учитывает посещение в выбранном периоде. Активность — проходы человека за 30 дней по{' '}
            {dateTime(index?.asOfMs)}, с учётом связей на дату прохода. Подразделение из Excel не считается
            подтверждённой бригадой компании.
          </p>
        </div>
      )}
      {hasFilters && (
        <div className="flex flex-wrap items-center gap-2" aria-label="Выбранные фильтры">
          {selected.map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() =>
                key === 'period'
                  ? set({ period: null, passFrom: null, passTo: null, cursor: null })
                  : update(key, null)
              }
              className="inline-flex min-h-9 items-center gap-2 rounded-full border border-line bg-subtle px-3 text-xs text-ink-2 hover:border-line-strong focus-visible:outline-2 focus-visible:outline-accent"
              aria-label={`Убрать фильтр: ${label}`}
            >
              {label}
              <X size={13} aria-hidden />
            </button>
          ))}
          <Button size="sm" variant="ghost" onClick={reset}>
            Сбросить всё
          </Button>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-ink-3">
        <p role="status" aria-live="polite">
          {attendance.error
            ? 'Выберите корректный период'
            : !rangeReady
              ? 'Обновляются сведения о проходах…'
              : index
                ? `Карточек: ${rows.length} из ${items.length}`
                : 'Справочник ещё не загружен'}
          {fetching && index ? ' · обновление…' : ''}
        </p>
        <p>По фамилии, А–Я</p>
      </div>
      {duplicateGroups > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-control bg-subtle px-3 py-2 text-xs text-ink-2">
          <p>
            Групп с совпадающими ФИО: {duplicateGroups}. Предварительные карточки разных площадок ещё не
            означают разных людей.
          </p>
          <Button size="sm" variant="ghost" onClick={() => showDuplicates()}>
            Показать совпадения
          </Button>
        </div>
      )}
      <div className="flex min-w-0 flex-col gap-3 md:flex-row">
        <nav
          aria-label="Алфавит фамилий"
          className="flex shrink-0 gap-1 overflow-x-auto rounded-card border border-line bg-subtle p-1 md:grid md:w-20 md:grid-cols-2 md:content-start md:gap-0.5 md:self-start md:overflow-visible"
        >
          <LetterButton
            label="Все"
            active={!letter}
            onClick={() => update('letter', null)}
            count={beforeLetter.length}
          />
          {LETTERS.map((l) => (
            <LetterButton
              key={l}
              label={l}
              active={letter === l}
              count={letterCounts.get(l) ?? 0}
              onClick={() => update('letter', l)}
            />
          ))}
        </nav>
        <div className="min-w-0 flex-1">
          <div
            className="mb-2 hidden grid-cols-[minmax(0,2fr)_minmax(0,1.5fr)_minmax(0,1fr)_auto] gap-4 px-4 text-xs text-ink-3 lg:grid"
            aria-hidden
          >
            <span>Человек / карточка</span>
            <span>Площадки и бригады</span>
            <span>{attendanceActive ? 'Последний проход в выборке' : 'Последний проход'}</span>
            <span className="w-16" />
          </div>
          {rows.length ? (
            <div
              ref={list}
              role="list"
              aria-label="Справочник персонала"
              onKeyDown={navigateRows}
              className="h-[65dvh] min-h-72 overflow-y-auto overscroll-contain rounded-card border border-line focus-visible:outline-2 focus-visible:outline-accent"
            >
              <div className="relative w-full" style={{ height: virtual.getTotalSize() }}>
                {virtualItems.map((v) => {
                  const p = rows[v.index];
                  if (!p) return null;
                  const sameName = nameCounts.get(p.nameKey) ?? 0;
                  const attendanceSites = p.passSites.filter(
                    (s) => !passSiteIds.length || passSiteIds.includes(s.id),
                  );
                  const rowSites = attendanceActive ? attendanceSites : p.sites;
                  const lastEventMs = attendanceActive
                    ? attendanceSites.reduce<number | null>(
                        (max, site) => Math.max(max ?? Number.NEGATIVE_INFINITY, site.lastEventMs),
                        null,
                      )
                    : p.lastEventMs;
                  return (
                    <div
                      key={p.id}
                      ref={virtual.measureElement}
                      data-index={v.index}
                      data-person-index={v.index}
                      role="listitem"
                      aria-posinset={v.index + 1}
                      aria-setsize={rows.length}
                      className="absolute top-0 left-0 grid w-full grid-cols-1 gap-2 border-b border-line px-4 py-3 hover:bg-subtle lg:grid-cols-[minmax(0,2fr)_minmax(0,1.5fr)_minmax(0,1fr)_auto] lg:items-center lg:gap-4"
                      style={{ transform: `translateY(${v.start}px)` }}
                    >
                      <div className="min-w-0 space-y-1">
                        <button
                          type="button"
                          data-open-person
                          className="text-left text-sm font-semibold break-words text-ink hover:text-accent focus-visible:outline-2 focus-visible:outline-accent max-sm:min-h-10"
                          aria-label={`Открыть карточку ${p.displayName ?? p.id}`}
                          onClick={() => onOpen(p.id)}
                        >
                          {p.displayName ?? 'Имя не получено'}
                        </button>
                        <div className="flex flex-wrap items-center gap-2">
                          <StateBadge state={p.state} />
                          <span className="font-mono text-xs text-ink-3" title={p.id}>
                            ID {p.id.slice(0, 8)}
                          </span>
                        </div>
                        {sameName > 1 && (
                          <button
                            type="button"
                            className="block min-h-7 text-left text-xs text-accent hover:underline max-sm:min-h-10"
                            onClick={() => showDuplicates(p.nameKey)}
                          >
                            Ещё {sameName - 1} с тем же ФИО
                          </button>
                        )}
                      </div>
                      <div className="min-w-0 space-y-1 text-xs text-ink-2">
                        <p className="break-words">
                          {rowSites.map((s) => s.name).join(' · ') || 'Нет регистраций на площадках'}
                        </p>
                        {p.brigades.length > 0 && (
                          <p className="break-words text-ink-3">
                            Бригада: {p.brigades.map((b) => b.name).join(' · ')}
                          </p>
                        )}
                        <span className="text-ink-3">Регистраций: {p.registrationCount}</span>
                        {p.employmentStatus !== 'unknown' && (
                          <div>
                            <Badge tone={p.employmentStatus === 'status_conflict' ? 'warn' : 'neutral'}>
                              {p.employmentStatus === 'dismissed'
                                ? 'Уволен'
                                : employmentLabels[p.employmentStatus]}
                            </Badge>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-ink-3">
                        <span className="lg:sr-only">Последний проход: </span>
                        {lastEventMs === null ? 'Проходов пока нет' : dateTime(lastEventMs)}
                      </p>
                      <Button
                        size="sm"
                        icon={PanelRightOpen}
                        className="max-lg:justify-self-start max-sm:h-10"
                        aria-label={`Показать сведения о ${p.displayName ?? p.id}`}
                        onClick={() => onOpen(p.id)}
                      >
                        Открыть
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : index && rangeReady ? (
            <div className="rounded-card border border-line">
              <NoResults />
              {hasFilters && (
                <div className="flex justify-center pb-5">
                  <Button onClick={reset}>Сбросить фильтры</Button>
                </div>
              )}
            </div>
          ) : null}
          <p className="mt-2 hidden text-xs text-ink-3 sm:block">
            В списке: ↑ и ↓ — соседняя карточка, Home и End — начало и конец. Enter открывает выбранную
            карточку.
          </p>
        </div>
      </div>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  children,
}: { label: string; value: string | null; onChange: (value: string) => void; children: ReactNode }) {
  return (
    <Field label={label}>
      <select
        className={inputCls}
        aria-label={label}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
      >
        {children}
      </select>
    </Field>
  );
}

function LetterButton({
  label,
  active,
  count,
  onClick,
}: { label: string; active: boolean; count: number; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label === 'Все' ? 'Все буквы' : `Фамилии на ${label}`}
      aria-pressed={active}
      title={`${label === 'Е' ? 'Е и Ё' : label} · ${count}`}
      disabled={!count && !active}
      onClick={onClick}
      className={`inline-flex h-10 min-w-10 shrink-0 items-center justify-center rounded-control px-1 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-accent disabled:cursor-default disabled:opacity-30 md:h-6 md:min-h-6 md:min-w-7 ${label === 'Все' ? 'md:col-span-2' : ''} ${active ? 'bg-accent text-white' : 'text-ink-2 hover:bg-canvas'}`}
    >
      {label}
    </button>
  );
}
